<script lang="ts" setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useBountySubmissionStore } from '../../stores/bounty-submission'
import { useWalletStore } from '../../stores/wallet'
import { formatIncidentType, formatDateTime, formatNumber } from '../../utils/format'

const bountyStore = useBountySubmissionStore()
const walletStore = useWalletStore()
const { form, isSubmitting } = storeToRefs(bountyStore)
const { pointsBalance } = storeToRefs(walletStore)

const durationLabel = computed(() => {
  if (!form.value.windowStartDateTime || !form.value.windowEndDateTime) return null
  const ms = form.value.windowEndDateTime.getTime() - form.value.windowStartDateTime.getTime()
  if (ms <= 0) return null
  const totalMinutes = Math.floor(ms / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} minutes`
  if (minutes === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`
  return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minutes`
})

const balanceAfterPosting = computed(() => {
  if (pointsBalance.value === null) return null
  return pointsBalance.value - form.value.rewardAmountPoints
})

defineExpose({})
</script>

<template>
  <div>
    <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9">Review your bounty</h6>
    <p class="text-body2 text-blue-grey-8 q-mb-md">
      Check the details below before posting. Credits will be deducted from your balance
      immediately.
    </p>

    <div class="row q-col-gutter-md">
      <!-- Location -->
      <div class="col-12 col-md-6">
        <q-card flat bordered class="bg-blue-grey-1 full-height">
          <q-card-section>
            <div class="text-overline text-blue-grey-6 q-mb-xs">Location</div>
            <div class="text-body2 text-blue-grey-9 q-mb-xs">
              <q-icon name="mdi-map-marker" color="blue-8" class="q-mr-xs" />
              {{ form.address?.rawInput || 'Location not set' }}
            </div>
            <div v-if="form.location" class="text-caption text-blue-grey-5">
              {{ form.location.lat }}, {{ form.location.lng }}
            </div>
            <div class="text-caption text-blue-grey-5">Matching radius: 100 metres</div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Date range -->
      <div class="col-12 col-md-6">
        <q-card flat bordered class="bg-blue-grey-1 full-height">
          <q-card-section>
            <div class="text-overline text-blue-grey-6 q-mb-xs">Date &amp; Time Window</div>
            <div class="text-body2 text-blue-grey-9">
              <div>
                <span class="text-weight-bold">Start:</span>
                {{ form.windowStartDateTime ? formatDateTime(form.windowStartDateTime) : '—' }}
              </div>
              <div>
                <span class="text-weight-bold">End:</span>
                {{ form.windowEndDateTime ? formatDateTime(form.windowEndDateTime) : '—' }}
              </div>
              <div v-if="durationLabel" class="text-caption text-blue-grey-5 q-mt-xs">
                Duration: {{ durationLabel }}
              </div>
              <div v-if="form.windowTimezone" class="text-caption text-blue-grey-5">
                Timezone: {{ form.windowTimezone }}
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Incident type -->
      <div class="col-12 col-md-6">
        <q-card flat bordered class="bg-blue-grey-1 full-height">
          <q-card-section>
            <div class="text-overline text-blue-grey-6 q-mb-xs">Incident Type</div>
            <q-chip v-if="form.incidentType" icon="mdi-tag" color="blue-8" text-color="white" dense>
              {{ formatIncidentType(form.incidentType) }}
            </q-chip>
            <span v-else class="text-blue-grey-5">—</span>
          </q-card-section>
        </q-card>
      </div>

      <!-- Reward -->
      <div class="col-12 col-md-6">
        <q-card flat bordered class="bg-blue-grey-1 full-height">
          <q-card-section>
            <div class="text-overline text-blue-grey-6 q-mb-xs">Reward</div>
            <q-chip icon="mdi-star-circle" color="blue-8" text-color="white">
              {{ formatNumber(form.rewardAmountPoints) }} pts
            </q-chip>
            <div v-if="balanceAfterPosting !== null" class="text-caption text-blue-grey-5 q-mt-xs">
              Balance after posting: {{ formatNumber(balanceAfterPosting) }} pts
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Description -->
      <div class="col-12">
        <q-card flat bordered class="bg-blue-grey-1">
          <q-card-section>
            <div class="text-overline text-blue-grey-6 q-mb-xs">Description</div>
            <div class="text-body2 text-blue-grey-9" style="white-space: pre-wrap">
              {{ form.requestDescription || '—' }}
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <q-inner-loading :showing="isSubmitting" label="Posting bounty..." />
  </div>
</template>
